import React from "react";
import { Button, Typography, Tag, Alert, Empty } from "antd";
import { ReloadOutlined, CheckCircleTwoTone, WarningOutlined } from "@ant-design/icons";

const { Text } = Typography;

/**
 * The error list under the grid. Errors only ever change when Re-validate is
 * pressed — editing a cell deliberately does NOT clear its error, because
 * whether an edit actually fixed anything is the server's call, not a guess
 * made here. Edited-but-unchecked cells are counted separately so it's clear
 * a re-check is owed.
 */
export default function ValidationPanel({
  validation, dirtyCount, validating, onRevalidate, rowIndexById, onFocusCell,
}) {
  const rowsWithErrors = validation
    ? validation.rows.filter((row) => row.errors.length > 0)
    : [];
  const errorCount = rowsWithErrors.reduce((sum, row) => sum + row.errors.length, 0);

  return (
    <div style={{ marginTop: 16, border: "1px solid #f0f0f0", borderRadius: 6, overflow: "hidden" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", background: "#fafafa", borderBottom: "1px solid #f0f0f0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!validation ? (
            <Text strong>Not validated yet</Text>
          ) : errorCount === 0 ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <CheckCircleTwoTone twoToneColor="#52C41A" style={{ fontSize: 16 }} />
              <Text strong style={{ fontSize: 13, lineHeight: 1 }}>All {validation.summary?.total || validation.rows?.length || 0} rows passed validation</Text>
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <WarningOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
              <Text strong style={{ fontSize: 13, lineHeight: 1 }}>
                {errorCount} error{errorCount > 1 ? "s" : ""} across {rowsWithErrors.length} row
                {rowsWithErrors.length > 1 ? "s" : ""}
              </Text>
            </span>
          )}
          {dirtyCount > 0 && (
            <Tag color="warning" style={{ marginLeft: 6 }}>
              {dirtyCount} edited cell{dirtyCount > 1 ? "s" : ""} not re-checked
            </Tag>
          )}
        </div>
        <Button
          type={dirtyCount > 0 || !validation ? "primary" : "default"}
          icon={<ReloadOutlined />}
          loading={validating}
          onClick={onRevalidate}
        >
          Re-validate
        </Button>
      </div>

      <div style={{ maxHeight: 150, overflowY: "auto", padding: "8px 14px" }}>
        {!validation ? (
          <Text type="secondary">Press Re-validate to check these rows against live reference data.</Text>
        ) : errorCount === 0 ? (
          dirtyCount > 0 ? (
            <Alert
              type="warning"
              showIcon
              message="These rows passed, but have been edited since — re-validate before submitting."
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nothing to fix — ready to submit."
              style={{ margin: "4px 0" }}
            />
          )
        ) : (
          rowsWithErrors.map((row) => (
            <div key={row.rowId} style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 12 }}>Row {rowIndexById[row.rowId] ?? "?"}</Text>
              <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
                {row.errors.map((error, index) => (
                  <li key={`${error.field}-${index}`} style={{ fontSize: 12, lineHeight: 1.6 }}>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => onFocusCell && onFocusCell(row.rowId, error.field)}
                      onKeyPress={(e) => { if (e.key === "Enter") onFocusCell && onFocusCell(row.rowId, error.field); }}
                      style={{ cursor: onFocusCell ? "pointer" : "default" }}
                    >
                      <Text type="danger" strong underline>{error.label}</Text>
                      <Text type="secondary"> — {error.message}</Text>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
